import API from '@/axios/API';
import CustomButton from '@/components/Common/Buttons/CustomButton';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider/GlobalSnackbarProvider';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Container,
    MenuItem,
    Paper,
    TextareaAutosize,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { settings, tickets } from '@/api';
import { tokens } from '@/theme/tokens';

// reCAPTCHA v2 site key. Public site key — safe to ship in client code. The
// matching secret key lives server-side only (used wherever the backend
// verifies the token via https://www.google.com/recaptcha/api/siteverify)
// and must never appear here.

/*Capctha Account Cretaed Via Mansi Inboxlogy Account

v3
Site Key - 6LfclzstAAAAAG4RxQA4kYRh2Xnf1OeuhIXj725E
Secret Key - 6LfclzstAAAAAOuYlHglh9k2ONHkIVUOeCMTJG9J

v2
Site Key - 6LclmzstAAAAALiapdI7Wq19ZV_oy-8t9WNFL3qE
Secret Key - 6LclmzstAAAAAKXjtFVFLIzKMJTNxeUUc46wanAZ
*/
const RECAPTCHA_SITE_KEY = '6LclmzstAAAAALiapdI7Wq19ZV_oy-8t9WNFL3qE';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ---------- Design tokens (visual styling copied from CompanyDetailsForm) ---------- */
const THEME = {
    primary: 'rgba(74, 102, 241, 1)',
    primarySoft: 'rgba(74, 102, 241, 0.08)',
    primaryBorder: 'rgba(74, 102, 241, 0.25)',
    surface: '#FFFFFF',
    inputDisabled: '#F5F5F7',
    border: '#EAEAEC',
    borderLight: '#F0F0F2',
    textPrimary: '#101114',
    textBody: '#3F3F46',
    textMuted: '#8A8A93',
    danger: '#DC2626',
};

const fieldLabelSx = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: THEME.textBody,
    mb: 0.75,
    display: 'block',
};

const textFieldSx = (enabled = true) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: enabled ? THEME.surface : THEME.inputDisabled,
        fontSize: '0.8125rem',
        transition: 'all 0.15s ease-in-out',
        '& fieldset': { borderColor: THEME.border },
        '&:hover fieldset': {
            borderColor: enabled ? THEME.primaryBorder : THEME.border,
        },
        '&.Mui-focused fieldset': {
            borderColor: THEME.primary,
            borderWidth: '1px',
            boxShadow: `0 0 0 3px ${THEME.primarySoft}`,
        },
        '&.Mui-disabled': { backgroundColor: THEME.inputDisabled },
    },
    '& .MuiInputBase-input': { py: 1.25, fontSize: '0.8125rem' },
    '& .MuiFormHelperText-root': {
        fontSize: '0.7rem',
        mx: 0,
        mt: 0.5,
        color: THEME.danger,
    },
});

// Same visual treatment as textFieldSx, applied to the plain <textarea>
// elements (Description / "Something else") via inline style + focus
// handlers, since TextareaAutosize isn't an MUI input and can't take sx.
const textareaStyle = {
    width: '100%',
    border: `1px solid ${THEME.border}`,
    borderRadius: '8px',
    padding: '12px 14px',
    fontFamily: 'inherit',
    fontSize: '0.8125rem',
    backgroundColor: THEME.surface,
    color: THEME.textPrimary,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    lineHeight: 1.5,
    transition: 'all 0.15s ease-in-out',
};

const handleTextareaFocus = (e) => {
    e.target.style.borderColor = THEME.primary;
    e.target.style.boxShadow = `0 0 0 3px ${THEME.primarySoft}`;
};

const handleTextareaBlur = (e) => {
    e.target.style.borderColor = THEME.border;
    e.target.style.boxShadow = 'none';
};

/**
 * SupportRequestPage
 *
 * Standalone public page (not a modal) for anonymous visitors to submit a
 * support ticket. Single-flow layout — every field is visible at once, in
 * order: email -> category -> subcategory -> description -> attachments ->
 * reCAPTCHA -> submit. No order-selection step, no login required.
 */
const SupportRequestPage = () => {
    const { showSnackbar } = useSnackbar();

    // --- Categories ---
    const [issueCategories, setIssueCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await settings.listIssueCategories();
                const sortedCategories = (data || []).map(cat => ({
                    ...cat,
                    subCategories: cat.subCategories
                        ? [...cat.subCategories].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }))
                        : []
                })).sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
                setIssueCategories(sortedCategories);
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, []);

    // --- Form fields ---
    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);

    const [category, setCategory] = useState('');
    const [issue, setIssue] = useState('');
    const [categoryElseText, setCategoryElseText] = useState('');

    const selectedCategory = issueCategories.find(c => c.id === category);
    const subCategories = selectedCategory?.subCategories || [];

    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // --- reCAPTCHA state ---
    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);

    const resetCaptcha = () => {
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
    };

    // File handlers
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const newAttachments = files.map(file => ({
            file,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };
    const handleRemoveAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // --- Validation (whole-form, since this is a single flow, not a wizard) ---
    const isEmailValid = EMAIL_PATTERN.test(email.trim());
    const isCategoryValid = category && category.trim() !== '' && (category !== 'something_else' || categoryElseText.trim() !== '');
    const isIssueValid = category === 'something_else' || (issue && issue.trim() !== '');
    const isDescriptionValid = description && description.trim() !== '';
    const isCaptchaValid = Boolean(captchaToken);

    const isFormValid = isEmailValid && isCategoryValid && isIssueValid && isDescriptionValid && isCaptchaValid;

    const handleCategoryChange = (e) => {
        const value = e.target.value;
        setCategory(value);
        setIssue(''); // reset dependent subcategory whenever category changes
    };

    const handleSubmit = async () => {
        setEmailTouched(true);
        if (!isFormValid) return;

        setLoading(true);
        try {
            // Upload attachments first to get CDN URLs.
            let attachmentsPayload = [];
            if (attachments.length > 0) {
                const uploadPromises = attachments.map(async ({ file }) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const uploadResp = await API.post(
                            `${import.meta.env.BACKEND_API}/uploadImages?key=${file.name}`,
                            formData,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                        );
                        if (uploadResp.status === 200 && uploadResp.data.fileUrl) {
                            return uploadResp.data.fileUrl;
                        }
                    } catch (uploadErr) {
                        console.error('File upload failed:', uploadErr);
                    }
                    return null;
                });
                const results = await Promise.all(uploadPromises);
                attachmentsPayload = results.filter(r => r !== null);
            }

            const selectedSubCategory = subCategories.find(s => s.id === issue);
            const ticketData = {
                // Fields matching POST /api/v1/workspace/{workspaceId}/support/ticket
                requesterEmail: email.trim(),
                subject: category === 'something_else'
                    ? 'Support - Something else'
                    : `${selectedCategory?.name || 'Support'} - ${selectedSubCategory?.name || issue}`,
                description: category === 'something_else'
                    ? `${categoryElseText}${description ? '\n\n' + description : ''}`
                    : description,
                issueCategoryId: category === 'something_else' ? null : category,
                issueSubCategoryId: category === 'something_else' ? null : issue,
                // No logged-in customer on a public page.
                customerId: null,
                channel: 'OTHER',
                // Order-selection step does not exist in this flow.
                orderIds: [],
                attachments: attachmentsPayload,
                // Backend must verify this token against
                // https://www.google.com/recaptcha/api/siteverify using the
                // SECRET key before accepting the ticket — the front end only
                // proves the checkbox was solved, not that the token is genuine.
                // captchaToken,
            };

            await tickets.createWebsiteTicket(ticketData)

            setSubmitted(true);
        } catch (error) {
            console.error('Error creating ticket:', error);
            showSnackbar('Failed to submit your request. Please try again.', 'error');
            resetCaptcha();
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitAnother = () => {
        setEmail('');
        setEmailTouched(false);
        setCategory('');
        setIssue('');
        setCategoryElseText('');
        setDescription('');
        setAttachments([]);
        resetCaptcha();
        setSubmitted(false);
    };

    if (submitted) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: tokens.color.bgDefault, py: { xs: 6, md: 10 } }}>
                <Container maxWidth="sm">
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: `${tokens.radius.cardLg}px`,
                            boxShadow: tokens.shadow.lg,
                            p: { xs: 4, md: 6 },
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.color.textDark, mb: 1.5 }}>
                            Request submitted
                        </Typography>
                        <Typography variant="body1" sx={{ color: tokens.color.textSecondary, mb: 4 }}>
                            Thanks — we've received your request and will follow up at {email || 'your email'} shortly.
                        </Typography>
                        <CustomButton onClick={handleSubmitAnother} variant="outlined" color="primary">
                            Submit another request
                        </CustomButton>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: tokens.color.bgDefault, py: { xs: 6, md: 10 } }}>
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: `${tokens.radius.cardLg}px`,
                        boxShadow: tokens.shadow.lg,
                        p: { xs: 3, md: 5 },
                    }}
                >
                    <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.textDark, mb: 1 }}>
                        Submit a request
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.color.textSecondary, mb: 4 }}>
                        Fields marked with an asterisk (*) are required.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Email */}
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Your email address
                                <Box component="span" sx={{ color: THEME.danger, ml: 0.25 }}>*</Box>
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => setEmailTouched(true)}
                                error={emailTouched && !isEmailValid}
                                helperText={emailTouched && !isEmailValid ? 'Enter a valid email address.' : ''}
                                sx={textFieldSx(true)}
                            />
                        </Box>

                        {/* Category */}
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                What do you need help with?
                                <Box component="span" sx={{ color: THEME.danger, ml: 0.25 }}>*</Box>
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={category}
                                onChange={handleCategoryChange}
                                disabled={categoriesLoading}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => {
                                        if (!selected) return <em style={{ color: THEME.textMuted }}>Select category</em>;
                                        if (selected === 'something_else') return 'Something else';
                                        const cat = issueCategories.find(c => c.id === selected);
                                        return cat ? cat.name : selected;
                                    }
                                }}
                                sx={textFieldSx(!categoriesLoading)}
                            >
                                <MenuItem disabled value="">
                                    <em>Select category</em>
                                </MenuItem>
                                {issueCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                ))}
                                <MenuItem value="something_else">Something else</MenuItem>
                            </TextField>

                            {category === 'something_else' && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography component="label" sx={fieldLabelSx}>
                                        Describe your issue
                                        <Box component="span" sx={{ color: THEME.danger, ml: 0.25 }}>*</Box>
                                    </Typography>
                                    <TextareaAutosize
                                        minRows={3}
                                        maxRows={6}
                                        placeholder="Tell us what's happening so we can route this to the right team."
                                        style={textareaStyle}
                                        onFocus={handleTextareaFocus}
                                        onBlur={handleTextareaBlur}
                                        value={categoryElseText}
                                        onChange={(e) => setCategoryElseText(e.target.value)}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Subcategory — only shown once a real category (not "something else") is picked */}
                        {category && category !== 'something_else' && (
                            <Box>
                                <Typography component="label" sx={fieldLabelSx}>
                                    Which issue best describes you?
                                    <Box component="span" sx={{ color: THEME.danger, ml: 0.25 }}>*</Box>
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={issue}
                                    onChange={(e) => setIssue(e.target.value)}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            if (!selected) return <em style={{ color: THEME.textMuted }}>Select subcategory</em>;
                                            const sc = subCategories.find(s => s.id === selected);
                                            return sc ? sc.name : selected;
                                        }
                                    }}
                                    sx={textFieldSx(true)}
                                >
                                    <MenuItem disabled value="">
                                        <em>Select subcategory</em>
                                    </MenuItem>
                                    {subCategories.map((sc) => (
                                        <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        {/* Description */}
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Description
                                <Box component="span" sx={{ color: THEME.danger, ml: 0.25 }}>*</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME.textMuted, mb: 1.5, fontSize: '0.75rem' }}>
                                Please enter the details of your request. A member of our support staff will respond as soon as possible.
                            </Typography>
                            <TextareaAutosize
                                minRows={5}
                                maxRows={10}
                                placeholder="Tell us what's going on..."
                                style={textareaStyle}
                                onFocus={handleTextareaFocus}
                                onBlur={handleTextareaBlur}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Box>

                        {/* Attachments */}
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Attachments <Box component="span" sx={{ color: THEME.textMuted, fontWeight: 400, textTransform: 'none' }}>(optional)</Box>
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {attachments.map((item, idx) => (
                                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, border: `1px solid ${tokens.color.border}`, borderRadius: 2, bgcolor: tokens.color.bgLight }}>
                                        <Avatar src={item.preview} variant="rounded" sx={{ width: 48, height: 48, borderRadius: 2 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{item.file.name}</Typography>
                                            <Typography variant="caption" sx={{ color: tokens.color.textSecondary }}>{(item.file.size / (1024 * 1024)).toFixed(2)} MB</Typography>
                                        </Box>
                                        <Button onClick={() => handleRemoveAttachment(idx)} size="small" sx={{ color: tokens.color.error }}>Remove</Button>
                                    </Box>
                                ))}

                                <Button
                                    component="label"
                                    fullWidth
                                    sx={{
                                        textTransform: "none",
                                        border: `2px dashed ${tokens.color.border}`,
                                        borderRadius: 2,
                                        py: 2,
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        gap: 2,
                                        color: tokens.color.textDark,
                                        '&:hover': { bgcolor: tokens.color.bgLight, borderColor: '#BDBDBD' }
                                    }}
                                >
                                    <UploadFileIcon sx={{ color: tokens.color.primary, ml: 1, fontSize: 28 }} />
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography sx={{ fontWeight: 600, color: tokens.color.textDark }}>
                                            Click to add {attachments.length > 0 ? 'more' : 'an'} attachment
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#757575' }}>Max 20 MB per file &middot; png, jpg, jpeg, csv, xlsx, pdf, svg</Typography>
                                    </Box>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".png, .jpg, .jpeg, .csv, .xlsx, .pdf, .svg"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            </Box>
                        </Box>

                        {/* reCAPTCHA */}
                        <Box>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={RECAPTCHA_SITE_KEY}
                                onChange={(token) => setCaptchaToken(token)}
                                onExpired={() => setCaptchaToken(null)}
                                onErrored={() => setCaptchaToken(null)}
                            />
                        </Box>

                        {/* Submit */}
                        <Box sx={{ pt: 1 }}>
                            <CustomButton
                                onClick={handleSubmit}
                                disabled={loading || !isFormValid}
                                variant="contained"
                                color="primary"
                                fullWidth
                                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </CustomButton>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default SupportRequestPage;